import { useMutation, useQuery, useQueryClient } from 'react-query';
import apiCalls from '../../utils/apiCalls';

export function useDelivery({ deliveryId, onSuccess }) {
  const { data, ...rest } = useQuery(
    ['delivery', deliveryId],
    () =>
      apiCalls
        .getDeliveries({
          deliveryId,
        })
        .then(({ data: { deliveries } }) => deliveries[0]),
    {
      enabled: !!deliveryId,
      onSuccess: (data) => {
        onSuccess(data);
      },
    }
  );
  return { delivery: data, ...rest };
}

export function useDeliveries({ routePlanId, ...other }) {
  const { data, ...rest } = useQuery(
    ['deliveries', routePlanId],
    () =>
      apiCalls
        .getDeliveries({
          routePlanId,
          ...other,
        })
        .then(({ data: { deliveries } }) => deliveries),
    {
      enabled: !!routePlanId,
    }
  );
  return { deliveries: data, ...rest };
}

export function useDeliveryCreate(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ delivery, deliveries }) =>
      apiCalls
        .createDeliveryV2({ delivery, deliveries })
        .then(({ data: { delivery, deliveries } }) => ({ delivery, deliveries })),
    {
      onSuccess: ({ delivery }) => {
        queryClient.invalidateQueries('deliveries');
        onSuccess(delivery);
      },
    }
  );
  return { createDelivery: mutate, ...rest };
}

export function useDeliveryEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ delivery }) => apiCalls.editDelivery({ delivery }).then(({ data: { delivery } }) => delivery),
    {
      onSuccess: (delivery) => {
        queryClient.invalidateQueries('routes');
        queryClient.invalidateQueries('deliveries');
        onSuccess(delivery);
      },
    }
  );
  return { editDelivery: mutate, ...rest };
}

export function useDeliveryDelete({ onSuccess = () => {} }) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(({ delivery }) => apiCalls.deleteDelivery({ deliveryId: delivery.id }), {
    onSuccess: () => {
      queryClient.invalidateQueries('deliveries');
      onSuccess();
    },
  });
  return { deleteDelivery: mutate, ...rest };
}

export function useSmsCreate(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation((data) => apiCalls.createSms(data).then(({ data: { sms } }) => sms), {
    onSuccess: (sms) => {
      queryClient.invalidateQueries('routes');
      onSuccess(sms);
    },
  });
  return { createSms: mutate, ...rest };
}

export function useRoute({ routeId, onSuccess }) {
  const { data, ...rest } = useQuery(
    ['route', routeId],
    () =>
      apiCalls
        .getRoutes({
          routeId,
        })
        .then(({ data: { routes } }) => routes[0]),
    {
      enabled: !!routeId,
      onSuccess: (data) => {
        onSuccess(data);
      },
    }
  );
  return { route: data, ...rest };
}

export function useRoutes({ routePlanId, refetchInterval }) {
  const { data, ...rest } = useQuery(
    ['routes', { routePlanId }],
    () =>
      apiCalls
        .getRoutes({
          routePlanId,
        })
        .then(({ data: { routes } }) => routes),
    {
      enabled: !!routePlanId,
      refetchInterval,
    }
  );
  return { routes: data, ...rest };
}

export function useRouteEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ route }) => apiCalls.editRoute({ route }).then(({ data: { route } }) => route),
    {
      onSuccess: (route) => {
        queryClient.invalidateQueries('routes');
        onSuccess(route);
      },
    }
  );
  return { editRoute: mutate, ...rest };
}

export function useRouteDelete({ onSuccess = () => {} }) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(({ route }) => apiCalls.deleteRoute({ routeId: route.id }), {
    onSuccess: () => {
      queryClient.invalidateQueries('routes');
      onSuccess();
    },
  });
  return { deleteRoute: mutate, ...rest };
}
